import {createTheme} from '@mui/material/styles';
import {colorSchemes} from './themePrimitives';

declare module '@mui/material/styles' {
    interface Palette {
        baseShadow: string;
    }

    interface PaletteOptions {
        baseShadow?: string;
    }

    interface TypeText {
        warning?: string;
    }
}

/**
 * ============================================================================
 * theme.ts — корневая тема Parallax
 * ============================================================================
 *
 * Структура:
 *   1. cssVariables — включает режим CSS-переменных (см. tokens.ts).
 *   2. colorSchemes — палитры light/dark из themePrimitives.ts.
 *   3. typography — шрифты и иерархия размеров из дизайн-системы §3.
 *   4. shape — общие радиусы.
 *   5. components — overrides для MUI-компонентов.
 *
 * Все cтили MUI-компонентов уровня "глобальные дефолты для всего приложения"
 * живут здесь. Точечные кастомизации (с особым поведением) — в MyButton,
 * MyTabs и т.д. Если стиль нужен ДЛЯ ВСЕХ <Button> в проекте, без исключений —
 * его место здесь.
 *
 * Цвета внутри styleOverrides ссылаются на CSS-переменные через строку
 * `var(--mui-palette-X)` — это паттерн, рекомендуемый MUI v6 для cssVariables.
 * theme.palette.* НЕ используем, т.к. оно запекается из light-схемы.
 * ============================================================================
 */
export const theme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data', // [data-mui-color-scheme="light|dark"] на <html>
    },

    colorSchemes,

    /**
     * Стандартный радиус скругления (используется MUI в Button, Card и т.д. по умолчанию).
     * Соответствует tokens.radius.md = 8.
     */
    shape: {
        borderRadius: 8,
    },

    typography: {
        fontFamily: `'IBM Plex Sans', 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif`,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 600,

        // Иерархия размеров из дизайн-системы §3.2
        h1: {fontSize: '3rem', fontWeight: 700, lineHeight: 1.2},      // 48px — маркетинг
        h2: {fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.25},  // 36px — маркетинг
        h3: {fontSize: '1.875rem', fontWeight: 600, lineHeight: 1.3},  // 30px — маркетинг
        h4: {fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.35},   // 24px — главные KPI
        h5: {fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4},   // 20px — заголовок страницы
        h6: {fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4},  // 18px — заголовок диалога

        subtitle1: {fontSize: '1rem', fontWeight: 500, lineHeight: 1.5},     // 16px — лейблы карточек
        subtitle2: {fontSize: '0.875rem', fontWeight: 500, lineHeight: 1.5}, // 14px — подзаголовки секций

        body1: {fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5},     // 14px — основной UI
        body2: {fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5},    // 13px — плотные таблицы

        caption: {fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4},    // 12px — подписи

        button: {textTransform: 'none', fontWeight: 500},                    // глобальный override
    },

    components: {
        // ====================================================================
        // CssBaseline
        // ====================================================================
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    // sub-pixel rendering уже задан в index.css,
                    // здесь ничего критичного добавлять не нужно
                },
            },
        },

        // ====================================================================
        // Button — глобальные дефолты для всех <Button>
        // (MyButton может дополнять или переопределять)
        // ====================================================================
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 8,
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                },
                sizeSmall: {
                    height: 32,
                    paddingInline: 12,
                    fontSize: '0.8125rem',
                },
                sizeMedium: {
                    height: 40,
                    paddingInline: 16,
                    fontSize: '0.875rem',
                },
                sizeLarge: {
                    height: 44,
                    paddingInline: 20,
                    fontSize: '0.9375rem',
                },
            },
        },

        // ====================================================================
        // TextField + OutlinedInput — единый стиль для всех input'ов в проекте
        // Это базовая стилизация. Specific компоненты (MyDatePicker, MyInput)
        // могут дополнять, но не переопределять цвета.
        // ====================================================================
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
            },
        },

        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    backgroundColor: 'var(--mui-palette-background-paper)',
                    transition: 'border-color 150ms, box-shadow 150ms, background-color 150ms',

                    '& fieldset': {
                        borderColor: 'var(--mui-palette-divider)',
                        transition: 'border-color 150ms',
                    },

                    '&:hover fieldset': {
                        borderColor: 'var(--mui-palette-text-disabled)',
                    },

                    '&.Mui-focused fieldset': {
                        borderColor: 'var(--mui-palette-primary-main)',
                        borderWidth: 1,
                    },

                    '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgb(var(--mui-palette-primary-mainChannel) / 0.15)',
                    },

                    // ----- WARNING-режим (свой ненавязчивый "пожалуйста, заполните") -----
                    // Активируется через data-warning="true" на корневом OutlinedInput.
                    // Работает на всём: TextField, Select, DatePicker (внутри они все
                    // используют OutlinedInput).
                    //
                    // Цвет — оранжевый (warning.main), мягче чем error.
                    // Используется для пустых обязательных полей, чтобы не пугать
                    // пользователя красным до того, как он попытался отправить форму.
                    '&[data-warning="true"] fieldset': {
                        borderColor: 'rgb(var(--mui-palette-warning-mainChannel) / 0.6)',
                    },

                    '&[data-warning="true"]:hover fieldset': {
                        borderColor: 'var(--mui-palette-warning-main)',
                    },

                    '&[data-warning="true"].Mui-focused fieldset': {
                        borderColor: 'var(--mui-palette-warning-main)',
                    },

                    '&[data-warning="true"].Mui-focused': {
                        boxShadow: '0 0 0 3px rgb(var(--mui-palette-warning-mainChannel) / 0.15)',
                    },

                    // ----- ERROR (после явной валидации submit) -----
                    '&.Mui-error fieldset': {
                        borderColor: 'var(--mui-palette-error-main)',
                    },

                    '&.Mui-error.Mui-focused': {
                        boxShadow: '0 0 0 3px rgb(var(--mui-palette-error-mainChannel) / 0.15)',
                    },

                    // ----- READ-ONLY (визуально похоже на disabled, но интерактивно для копирования) -----
                    // Активируется через data-readonly="true". Отличается от disabled тем, что
                    // текст можно выделить и скопировать; курсор не "not-allowed".
                    //
                    // Высота при этом ОСТАЁТСЯ той же (40px) — поэтому переключение
                    // edit ↔ read не сдвигает форму.
                    '&[data-readonly="true"]': {
                        backgroundColor: 'var(--mui-palette-action-disabledBackground)',
                        cursor: 'default',
                    },

                    '&[data-readonly="true"] fieldset': {
                        borderColor: 'var(--mui-palette-divider)',
                    },

                    '&[data-readonly="true"]:hover fieldset': {
                        // в read-only hover НЕ должен подсвечивать рамку
                        borderColor: 'var(--mui-palette-divider)',
                    },

                    // В read-only ОТМЕНЯЕМ фокус-эффект: ни рамки primary, ни glow-кольца.
                    // Поле выглядит как индикатор, а не как интерактивный input —
                    // это честнее по отношению к пользователю.
                    '&[data-readonly="true"].Mui-focused': {
                        boxShadow: 'none',
                    },
                    '&[data-readonly="true"].Mui-focused fieldset': {
                        borderColor: 'var(--mui-palette-divider)',
                        borderWidth: 1,
                    },

                    // ----- DISABLED (реально отключён, нельзя взаимодействовать) -----
                    '&.Mui-disabled': {
                        backgroundColor: 'var(--mui-palette-action-disabledBackground)',
                        cursor: 'not-allowed',
                    },
                },
                input: {
                    fontVariantNumeric: 'tabular-nums',
                    // Высота 40px (size=small) минус 2*1px бордера = 38px доступного контента
                    // Внутри input MUI ставит padding автоматически — не трогаем
                },
            },
        },

        // InputLabel — лейбл над/в поле ввода
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: 'var(--mui-palette-text-secondary)',
                    fontSize: '0.875rem',

                    '&.Mui-focused': {
                        color: 'var(--mui-palette-primary-main)',
                    },

                    '&.Mui-error': {
                        color: 'var(--mui-palette-error-main)',
                    },
                },
            },
        },

        // FormHelperText — текст ошибки / подсказки под полем
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    marginLeft: 0,
                    marginTop: 4,
                    fontSize: '0.75rem',
                    minHeight: '1em', // резервируем место — текст не "прыгает" при появлении

                    '&.Mui-error': {
                        color: 'var(--mui-palette-error-main)',
                    },
                },
            },
        },

        // ====================================================================
        // Card — карточка с лёгкой границей вместо тени
        // ====================================================================
        MuiCard: {
            defaultProps: {
                elevation: 0, // тень не нужна — отделяемся бордером
            },
            styleOverrides: {
                root: {
                    backgroundColor: 'var(--mui-palette-background-paper)',
                    border: '1px solid var(--mui-palette-divider)',
                    borderRadius: 12,
                    backgroundImage: 'none', // MUI v6 в dark кладёт overlay через background-image — отключаем
                },
            },
        },

        MuiCardHeader: {
            styleOverrides: {
                root: {
                    padding: '20px 24px 8px',
                },
                title: {
                    fontSize: '1.125rem',
                    fontWeight: 600,
                },
            },
        },

        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '16px 24px',
                    '&:last-child': {paddingBottom: 24},
                },
            },
        },

        // ====================================================================
        // Paper — основа для popover/menu/dialog
        // ====================================================================
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none', // см. выше про overlay в dark
                },
            },
        },

        // ====================================================================
        // Dialog
        // ====================================================================
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 12,
                    backgroundColor: 'var(--mui-palette-background-paper)',
                    backgroundImage: 'none',
                },
            },
        },

        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    padding: '20px 24px 12px',
                },
            },
        },

        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '8px 24px 16px',
                },
            },
        },

        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '12px 24px 20px',
                    gap: 8,
                },
            },
        },

        // ====================================================================
        // Menu / MenuItem — для Select, контекстных меню
        // ====================================================================
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 8,
                    border: '1px solid var(--mui-palette-divider)',
                    marginTop: 4,
                    backgroundImage: 'none',
                },
                list: {
                    padding: 4,
                },
            },
        },

        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontSize: '0.875rem',
                    minHeight: 36,
                    paddingInline: 12,

                    // && — для перебивания MUI'шного .MuiButtonBase-root.MuiMenuItem-root.Mui-selected (0,3,0)
                    '&&.Mui-selected': {
                        backgroundColor: 'var(--mui-palette-action-selected)',
                        color: 'var(--mui-palette-text-primary)',

                        '&:hover': {
                            backgroundColor: 'var(--mui-palette-action-selected)',
                        },
                    },

                    '&:hover': {
                        backgroundColor: 'var(--mui-palette-action-hover)',
                    },
                },
            },
        },

        // ====================================================================
        // Tooltip
        // ====================================================================
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: 'var(--mui-palette-grey-800)',
                    color: 'var(--mui-palette-common-white)',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    padding: '6px 10px',
                    borderRadius: 6,
                },
                arrow: {
                    color: 'var(--mui-palette-grey-800)',
                },
            },
        },

        // ====================================================================
        // Divider
        // ====================================================================
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: 'var(--mui-palette-divider)',
                },
            },
        },

        // ====================================================================
        // Link
        // ====================================================================
        MuiLink: {
            defaultProps: {
                underline: 'none',
            },
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'var(--mui-palette-text-secondary)',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    borderRadius: 6, // t.radius.sm — для фокус-кольца
                    transition: 'color 120ms',
                    '&:hover': {
                        color: 'var(--mui-palette-text-primary)',
                        textDecoration: 'none',
                    },
                    '&:focus-visible': {
                        outline: 'none',
                        // focus-ring: 3px primary @ alpha(0.25), как у MyButton
                        boxShadow: '0 0 0 3px rgb(var(--mui-palette-primary-mainChannel) / 0.25)',
                    },
                },
            },
        },
    },
});